import React from 'react';
import { Box, Typography, useTheme, alpha, Tooltip } from '@mui/material';

const CashFlowMiniChart = ({ data = [] }) => {
  const theme = useTheme();
  const maxValue = Math.max(...data.map(item => Math.abs(item.value || 0)), 100);
  const yAxisMax = Math.ceil(maxValue / 50000) * 50000;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <Box sx={{ 
      height: 350,
      bgcolor: '#ffffff',
      borderRadius: 3,
      border: '1px solid #e0e0e0',
      p: 2,
      position: 'relative'
    }}>
      {/* Chart Container */}
      <Box sx={{ 
        height: '100%',
        display: 'flex',
        position: 'relative'
      }}>
        {/* Y-Axis */}
        <Box sx={{ 
          width: 80,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          pr: 2,
          pb: 4
        }}>
          {[
            yAxisMax,
            Math.round(yAxisMax * 0.75),
            Math.round(yAxisMax * 0.5),
            Math.round(yAxisMax * 0.25),
            0
          ].map((value, idx) => (
            <Typography key={idx} sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#333' }}>
              ${value.toLocaleString()}
            </Typography>
          ))}
        </Box>

        {/* Chart Area */}
        <Box sx={{ 
          flex: 1,
          height: '100%',
          position: 'relative',
          pb: 4
        }}>
          {/* Grid Lines */}
          <Box sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 32,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            zIndex: 1
          }}>
            {Array.from({ length: 5 }, (_, i) => (
              <Box key={i} sx={{ 
                height: '1px', 
                bgcolor: '#e0e0e0',
                width: '100%'
              }} />
            ))}
          </Box>

          {/* Bars Container */}
          <Box sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 32,
            display: 'grid',
            gridTemplateColumns: `repeat(${data.length}, 1fr)`,
            gap: 1,
            alignItems: 'end',
            zIndex: 2
          }}>
            {data.map((item, index) => {
              const heightPercentage = yAxisMax > 0 ? (Math.abs(item.value) / yAxisMax) * 100 : 0;
              const isPositive = item.value >= 0;
              
              return (
                <Tooltip 
                  key={index}
                  title={`${item.label}: ${isPositive ? '+' : '-'}${formatCurrency(Math.abs(item.value))}`} 
                  placement="top"
                  arrow
                >
                  <Box sx={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    height: '100%',
                    alignItems: 'end'
                  }}>
                    <Box sx={{
                      width: '70%',
                      maxWidth: 60,
                      height: `${heightPercentage}%`,
                      minHeight: item.value !== 0 ? '4px' : '0px',
                      backgroundColor: isPositive ? '#4CAF50' : '#F44336',
                      borderRadius: '4px 4px 0 0',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: isPositive ? '#45a049' : '#da190b'
                      }
                    }} />
                  </Box>
                </Tooltip>
              );
            })}
          </Box>

          {/* Labels */}
          <Box sx={{ 
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 32,
            display: 'grid',
            gridTemplateColumns: `repeat(${data.length}, 1fr)`,
            gap: 1,
            alignItems: 'center'
          }}>
            {data.map((item, index) => (
              <Typography 
                key={index}
                sx={{ 
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#666',
                  textAlign: 'center'
                }}
              >
                {item.label}
              </Typography>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CashFlowMiniChart;

